import React, { FC, useState, useEffect } from "react";
import styled, { keyframes, StyledComponent } from "styled-components";

import PresentationEditorController from "../../PresentationEditor/PresentationEditorController";
import { ScrollBox } from "js/react/components/LayoutGrid";
import { UIPane } from "js/react/components/UiComponents";

const skeletonThemeColors = {
    shimmerFrom: "#E0E0E0",
    shimmerFrom2: "#EBEBEB",
    shimmerFrom3: "#F1F1F1",
    shimmerTo: "#E0E0E0",
};

const shimmer = keyframes`
    0% {
        background-position: -1000px 0;
    }
    100% {
        background-position: 1000px 0;
    }
`;

const SkeletonContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;    
`;

const SkeletonBox = styled.div`
    background: linear-gradient(
        90deg,
        ${skeletonThemeColors.shimmerFrom} 0%,
        ${skeletonThemeColors.shimmerFrom2} 25%,
        ${skeletonThemeColors.shimmerFrom3} 65%,
        ${skeletonThemeColors.shimmerTo} 100%
    );
    background-size: 1000px 100%;
    animation: ${shimmer} 2s ease-out infinite;
`;

const SlidePreview = styled(SkeletonBox)`
    aspect-ratio: 16 / 9;
    flex-shrink: 0;
`;

const SlideLabel = styled.div`
    width: 165px;
    height: 23px;
    background: #E0E0E0;
`;

const OptionButton = styled.div`
    height: 24px;
    width: 24px;
    background: #E0E0E0;
`;

const SlidesList = styled.div`
    width: 100%;
    background: white;
    padding: 20px;
    margin: 0px 0px 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

const StyledThumbnailGrid = styled.div`
    padding: 2px;
    display: grid;
    grid-column-gap: 30px;
    grid-row-gap: 30px;
    grid-auto-rows: max-content;
    
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-columns: repeat(1, minmax(0, 1fr));
    @media(min-width: 0px) {
    }
    @media(min-width: 750px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    @media(min-width: 1200px){
      grid-template-columns: repeat(3, minmax(0, 1fr));;
    }
    @media(min-width: 1700px){
      grid-template-columns: repeat(4, minmax(0, 1fr));;
    }
`;

const SlidePreviewContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
`;

const OptionButtons = styled.div`
    display: flex;
    gap: 24px;
    direction: row;
    margin-right: 28px;
`;

const SlideContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const StyledUIPane = styled(UIPane)`
    gap: 24px;
    display: flex;
    flex-direction: column;
    padding-top: 24px;
` as unknown as StyledComponent<"div", any, {}, never>;

const LibrarySkeleton: React.FC = () => {
    // create 15 slide container elements so we have enough to fill the screen and show the scrollbar (shouldn't be actively used!)
    const slideContainerElements = Array.from({ length: 15 }, (_, index) => (
        <SlideContainer key={index}>
            <SlidePreview />
            <SlideLabel />
        </SlideContainer>
    ));

    return (
        <SkeletonContainer>
            <StyledUIPane>
                <SlidePreviewContainer>
                    <SlideLabel />
                    <OptionButtons>
                        <OptionButton />
                        <OptionButton />
                        <OptionButton />
                    </OptionButtons>
                </SlidePreviewContainer>
                <ScrollBox fillWidth fillHeight style={{ pointerEvents: "none" }}>
                    <SlidesList>
                        <StyledThumbnailGrid>
                            {slideContainerElements}
                        </StyledThumbnailGrid>
                    </SlidesList>
                </ScrollBox>
            </StyledUIPane>
        </SkeletonContainer>
    );
};

export default LibrarySkeleton;
